import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, FileText } from 'lucide-react';
import Layout from '../components/Layout';
import { bookingsAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { formatDateUK } from '../utils/dateUtils';

// Read-only summary of all bookings for one customer.
// After the flatten refactor, each "row" in the table is one flat Booking document.
const BookingDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCustomerBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const loadCustomerBookings = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.forCustomer(customerId);
      const list = response.data || [];
      setBookings(list);
      // Customer info comes back populated on each booking
      const firstCustomer = list[0]?.customer;
      setCustomer(firstCustomer && typeof firstCustomer === 'object' ? firstCustomer : null);
    } catch (err) {
      console.error('Error loading customer bookings:', err);
      setError('Failed to load bookings');
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = value => `£${(value || 0).toFixed(2)}`;
  const formatDate = dateString => formatDateUK(dateString);

  const totalValue = bookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0);

  if (loading) {
    return (
      <Layout>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 mt-2">Loading booking details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || bookings.length === 0) {
    return (
      <Layout>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {error || 'This customer has no bookings yet.'}
            </p>
            <div className="mt-6">
              <Link
                to="/bookings"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Bookings
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const oldestCreatedAt = bookings.reduce(
    (oldest, b) =>
      !oldest || new Date(b.createdAt) < new Date(oldest) ? b.createdAt : oldest,
    null
  );

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/bookings')}
                className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Bookings
              </button>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to={`/bookings/customer/${customerId}`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Link>
            </div>
          </div>

          <div className="mt-4">
            <h1 className="text-3xl font-bold text-gray-900">Booking Details</h1>
            <p className="mt-2 text-gray-600">
              {oldestCreatedAt && `First created on ${formatDate(oldestCreatedAt)}`}
            </p>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-8">
                <div>
                  <div className="text-sm font-medium text-gray-500">Customer</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {customer?.name || 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Total Magazines</div>
                  <div className="text-lg font-semibold text-gray-900">{bookings.length}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Total Amount</div>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(totalValue)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Magazine Entries */}
        <div className="mt-6">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Magazine Entries</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Details of all magazine bookings for this customer
              </p>
            </div>
            <div className="border-t border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Magazine</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Size</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">List</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Discount %</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Discount £</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Add Charge £</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Start</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Artwork Note</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookings.map(booking => {
                      const firstSelected =
                        (booking.monthlyArtwork || []).find(a => a.isSelected) || null;
                      const selectedCount = (booking.monthlyArtwork || []).filter(
                        a => a.isSelected
                      ).length;

                      return (
                        <React.Fragment key={booking._id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm font-medium text-gray-900">
                                {booking.magazine?.name || 'N/A'}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm text-gray-900">
                                {booking.contentSize?.adSize?.name || 'N/A'}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm text-gray-900">
                                {booking.contentType || 'N/A'}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm text-gray-900">
                                {formatCurrency(booking.listPrice || 0)}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm text-gray-900">
                                {booking.discountPercentage || 0}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm text-gray-900">
                                {formatCurrency(booking.discountValue || 0)}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm text-gray-900">
                                {formatCurrency(booking.additionalCharge || 0)}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(booking.totalPrice)}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm text-gray-900">
                                {firstSelected?.month || booking.startIssue || 'N/A'}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-top">
                              <div className="text-sm text-gray-900">
                                {selectedCount > 0
                                  ? `${selectedCount} months selected`
                                  : 'N/A'}
                              </div>
                            </td>
                          </tr>
                          {selectedCount > 0 && (
                            <tr>
                              <td colSpan="10" className="px-4 py-2 bg-gray-50">
                                <div className="border border-gray-200 rounded-lg p-4">
                                  <div
                                    className="grid gap-4 text-xs font-medium text-gray-500 mb-2"
                                    style={{ gridTemplateColumns: '80px 1fr 1fr 40px' }}
                                  >
                                    <div>Issue</div>
                                    <div>Note</div>
                                    <div>Filename</div>
                                    <div></div>
                                  </div>
                                  {booking.monthlyArtwork
                                    .filter(a => a.isSelected)
                                    .map((artwork, idx) => (
                                      <div
                                        key={idx}
                                        className="grid gap-4 text-sm py-1 border-b border-gray-100 last:border-b-0"
                                        style={{ gridTemplateColumns: '80px 1fr 1fr 40px' }}
                                      >
                                        <div className="text-gray-900">{artwork.month}</div>
                                        <div className="text-gray-600">
                                          {artwork.artworkNote || ''}
                                        </div>
                                        <div className="text-gray-600">
                                          {artwork.filename || ''}
                                        </div>
                                        <div></div>
                                      </div>
                                    ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Notes Section — show notes from each booking that has them */}
        {bookings.some(b => b.notes) && (
          <div className="mt-6">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Notes</h3>
                <div className="mt-2 space-y-2 text-sm text-gray-700">
                  {bookings
                    .filter(b => b.notes)
                    .map(b => (
                      <div key={b._id}>{b.notes}</div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BookingDetails;
